// IDE du CodeController — WEB. Coquille FINE au-dessus du paquet partagé @cc-titans/cc-jaw-ide :
// toute la logique de l'éditeur (coloration, autocomplétion, débogage, thèmes, Run/Debug Pyodide)
// vit dans le paquet — corrigée une seule fois, pour l'app ET le back-office. Ici on ne fait que
// BRANCHER la donnée : instancier le projet du candidat et câbler les mutations sur le backend
// runtime (api/projects) + les réglages sur le profil (api/prefs).
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { CodeIde, CodeIdeAdapter, IdeFile } from '@cc-titans/cc-jaw-ide';

import { addProjectFile, deleteProjectFile, deleteProjectDir, getProject, instantiateProject,
         moveProjectFile, renameProjectFile, renameProjectDir, recordRun, saveProjectFile,
         Project } from '../api/projects';
import { getIdeSettings, putIdeSettings } from '../api/prefs';
import { useAuth } from '../context/AuthContext';
import { getUsSolution } from '../api/userStories';
import { TYPE_COLOR } from '../utils/cyb';
import HeaderNav from '../components/HeaderNav';
import { useT } from '../i18n/useT';

const ACCENT = TYPE_COLOR.task;

export default function CodeIdeScreen({ navigation, route }: any = {}) {
  const t = useT();
  const { getAccessToken } = useAuth();
  const templateId: number | undefined = route?.params?.projectTemplateId;
  const missionId: number | undefined = route?.params?.missionId;
  const elementId: string = String(route?.params?.itemId ?? '');
  // Mode SOLUTION (CCT-100) : on n'instancie pas un projet du candidat, on AFFICHE la
  // template de référence. C'est le serveur qui autorise (tâche validée ou abandonnée).
  const solutionOf: string | undefined = route?.params?.solutionOf;

  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState('');
  const projectRef = useRef<Project | null>(null); projectRef.current = project;

  useEffect(() => {
    void (async () => {
      if (solutionOf && missionId) {
        try {
          const sol = await getUsSolution(getAccessToken(), missionId, solutionOf);
          // Adapté au contrat `Project` : mêmes champs, fichiers tous en lecture seule.
          setProject({
            projectid: -1, language: sol.language ?? 'python', entrypoint: sol.entrypoint ?? '',
            allowFolders: false, allowUpload: false,
            files: sol.files.map(f => ({ ...f, mode: 'read_only' })),
          } as any);
        } catch (e: any) { setError(String(e?.message ?? e)); }
        return;
      }
      if (!templateId || !missionId) { setError("{t('code.noTemplate')}"); return; }
      try { setProject(await instantiateProject(getAccessToken(), templateId, missionId, elementId)); }
      catch (e: any) { setError(String(e?.message ?? e)); }
    })();
  }, [templateId, missionId, elementId, solutionOf]);

  if (error && !project) {
    return <SafeAreaView style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: '#ff2d55', textAlign: 'center', maxWidth: 420 }}>{error}</Text></SafeAreaView>;
  }
  if (!project) {
    return <SafeAreaView style={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10 }}><ActivityIndicator color={ACCENT} /><Text style={{ color: '#8892a4' }}>{t('code.preparing')}</Text></SafeAreaView>;
  }

  const pid = project.projectid;
  const token = () => getAccessToken();
  // Recharge la source de vérité (arbre + logs) depuis le backend après une mutation structurelle.
  const refresh = async () => { setProject(await getProject(token(), projectRef.current!.projectid)); };

  const adapter: CodeIdeAdapter = {
    files: project.files.map((f): IdeFile => ({ path: f.path, mode: f.mode, content: f.content })),
    caps: { allowFolders: project.allowFolders, allowUpload: project.allowUpload },
    language: project.language,
    entrypoint: project.entrypoint,
    onAddFile: async (path, content, uploaded) => { const r = await addProjectFile(token(), pid, path, content, uploaded); await refresh(); return r; },
    // Sauvegarde : on ne recharge PAS tout l'arbre (débounce à 700 ms) — patch local du contenu.
    // En mode solution, toute écriture est un no-op : la template n'appartient pas au joueur.
    onSaveFile: solutionOf ? (async () => {}) : async (path, content) => { await saveProjectFile(token(), pid, path, content); setProject((p) => (p ? { ...p, files: p.files.map((f) => f.path === path ? { ...f, content } : f) } : p)); },
    onDeleteFile: async (path) => { await deleteProjectFile(token(), pid, path); await refresh(); },
    onRenameFile: async (path, newName) => { const r = await renameProjectFile(token(), pid, path, newName); await refresh(); return r; },
    onMoveFile: async (path, toDir) => { const r = await moveProjectFile(token(), pid, path, toDir); await refresh(); return r; },
    onRenameDir: async (path, newName) => { const r = await renameProjectDir(token(), pid, path, newName); await refresh(); return r; },
    onDeleteDir: async (path) => { await deleteProjectDir(token(), pid, path); await refresh(); },
    // Archive l'exécution ET rafraîchit (le serveur écrit logs/run-N.log).
    onRecordRun: async (outcome) => { await recordRun(token(), pid, outcome); await refresh(); },
    settings: {
      load: async () => { try { return (await getIdeSettings(token())).ide; } catch { return null; } },
      save: async (sset) => { await putIdeSettings(token(), sset); },
    },
  };

  return (
    // CCT-143 — l'IDE est rendu DANS un ScrollView de react-native-web (mesuré : 681 px de
    // haut pour 1234 px de contenu) : c'est LUI qui défilait, pas la page, et la bande de
    // débogage vivait à son extrémité basse. Le borner à `100dvh` aggravait même le cas — on
    // forçait 800 px dans un conteneur qui n'en fait que 681.
    // `position: fixed` sort l'écran du flux de CE conteneur : l'IDE occupe la fenêtre, quel
    // que soit l'ancêtre qui défile. Choisi plutôt que de traquer le ScrollView dans la pile de
    // navigation — l'écran est déjà plein cadre et porte son propre bouton retour.
    <SafeAreaView style={{ position: 'fixed' as any, top: 0, right: 0, bottom: 0, left: 0,
      zIndex: 20, minHeight: 0, overflow: 'hidden', backgroundColor: '#0a0e1a' }}>
      <CodeIde adapter={adapter}
        onBack={() => navigation?.goBack?.()}
        headerRight={<HeaderNav onPrev={undefined} onNext={() => navigation?.goBack?.()} accent={ACCENT} isLast />} />
    </SafeAreaView>
  );
}
